import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { reviewPricingLine } from "./copy/companyBrainCopy";
import "./Landing.css";

export const CONTACT_EMAIL = "renderrepo@gmail.com";

const WORKFLOW_STEPS = [
  [
    "Map the workflow",
    "We walk through how the business handles one real task today—who does what, where things stall, and what the owner needs to stay in control.",
  ],
  [
    "Identify one useful improvement",
    "Together we pick a single change that is worth doing now: a practical AI-assisted workflow or a small tool, not a full rebuild.",
  ],
  ["Build or prototype it", "We implement a fixed-scope fix the owner can try against real work."],
  [
    "Test it with the owner",
    "The owner reviews customer-facing steps before anything goes out. AI does not send messages or make commitments on its own.",
  ],
  [
    "Clear handoff",
    "You leave with what was built, how to use it, and what to watch next—without being locked into a long consulting engagement.",
  ],
];

const WORKFLOW_EXAMPLES = [
  "Organizing incoming estimate requests",
  "Drafting follow-up messages for the owner to approve",
  "Summarizing job notes after a site visit",
  "Tracking next steps so nothing sits idle",
];

const MAIL = {
  workflow: {
    subject: "Workflow help — 20-minute call",
    body:
      "Hi Ryan,\n\nI am interested in workflow help for my business.\n\nBusiness:\nCity:\nThe repetitive task I want to improve:\n\n",
  },
  review: {
    subject: "Software acceptance review",
    body:
      "Hi Ryan,\n\nI need a software change reviewed against approved requirements.\n\nRepository or PR (if ready):\nWhat should stay true after the change:\n\n",
  },
};

/** Build a mailto URL from fixed, trusted copy only (never user input). */
export function mailtoFor(interest) {
  const entry = MAIL[interest];
  if (!entry) return `mailto:${CONTACT_EMAIL}`;
  return `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(entry.subject)}&body=${encodeURIComponent(entry.body)}`;
}

const PAGE_TITLE = "RenderRepo | Practical AI for small businesses and software acceptance review";
const PAGE_DESCRIPTION =
  "Hands-on workflow help for owner-run Indianapolis businesses, plus RenderRepo—software acceptance review that checks code changes against what was approved.";

export default function Landing() {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = PAGE_TITLE;
    const meta = document.querySelector('meta[name="description"]');
    const previousDescription = meta?.getAttribute("content") || "";
    if (meta) meta.setAttribute("content", PAGE_DESCRIPTION);
    return () => {
      document.title = previousTitle;
      if (meta) meta.setAttribute("content", previousDescription);
    };
  }, []);

  return (
    <div className="landing-page">
      <nav className="landing-nav" aria-label="Marketing">
        <div className="landing-nav-inner">
          <a href="#top" className="landing-nav-brand">
            RenderRepo
          </a>
          <div className="landing-nav-actions">
            <a href="#workflow" className="landing-nav-link">
              Workflow help
            </a>
            <a href="#renderrepo" className="landing-nav-link">
              RenderRepo
            </a>
            <Link to="/login" className="landing-nav-link">
              Sign in
            </Link>
            <Link to="/register" className="landing-nav-cta">
              Register
            </Link>
          </div>
        </div>
      </nav>

      <header className="landing-hero" id="top">
        <div className="landing-hero-content">
          <p className="landing-hero-eyebrow">Owner-run businesses in Indianapolis</p>
          <h1 className="landing-hero-title">Practical AI and workflow improvements for small businesses.</h1>
          <p className="landing-hero-subtext">
            We help business owners find one repetitive, time-consuming process, improve it with a practical
            AI-assisted workflow or small tool, and make sure the owner stays in control.
          </p>
          <div className="landing-hero-ctas">
            <a href={mailtoFor("workflow")} className="landing-cta landing-cta-primary">
              Book a 20-minute workflow call
            </a>
            <a href="#renderrepo" className="landing-cta landing-cta-secondary">
              Explore RenderRepo
            </a>
          </div>
          <p className="landing-hero-trust">
            Founder-led. Not every business needs AI or a custom app—we start with one real task worth improving.
          </p>
        </div>
      </header>

      <main>
        <section className="landing-section landing-offer landing-offer--service" id="workflow" aria-labelledby="workflow-heading" tabIndex={-1}>
          <div className="landing-section-inner">
            <p className="landing-section-eyebrow">Hands-on service</p>
            <h2 id="workflow-heading" className="landing-section-title">
              Workflow help for owner-run businesses
            </h2>
            <p className="landing-proof-lede">
              The near-term way to work with us is a short engagement: we learn how you handle a real task today,
              then improve that one path. You do not need RenderRepo—or any software product—to get this help.
            </p>

            <ol className="landing-pain-grid landing-workflow-steps">
              {WORKFLOW_STEPS.map(([title, body], index) => (
                <li key={title} className="landing-pain-card">
                  <h3 className="landing-pain-card-title">
                    {index + 1}. {title}
                  </h3>
                  <p>{body}</p>
                </li>
              ))}
            </ol>

            <div className="landing-readable-panel landing-examples-block">
              <h3 className="landing-pain-card-title">Examples of tasks we often start with</h3>
              <ul className="landing-example-list">
                {WORKFLOW_EXAMPLES.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>

            <article className="landing-pain-card landing-sprint-card">
              <p className="landing-section-eyebrow">Introductory service</p>
              <h3 className="landing-pain-card-title">Workflow Fix Sprint</h3>
              <p>
                A fixed-scope engagement to map one workflow, ship a practical improvement, test it with the owner,
                and hand it off clearly. Scope is agreed up front so the work stays bounded.
              </p>
              <p className="landing-cta-row">
                <a href={mailtoFor("workflow")} className="landing-cta landing-cta-primary">
                  Book a 20-minute workflow call
                </a>
              </p>
            </article>
          </div>
        </section>

        <section className="landing-section landing-section--surface" id="connect" aria-labelledby="connect-heading" tabIndex={-1}>
          <div className="landing-section-inner landing-readable-panel">
            <p className="landing-section-eyebrow">How the two parts connect</p>
            <h2 id="connect-heading" className="landing-section-title">
              Advice first. Software review when you build.
            </h2>
            <p className="landing-proof-lede">
              A business can get workflow advice without using RenderRepo. When the work involves custom software, we
              help define what should be built and—using RenderRepo—check the delivered result against that agreed
              scope and the evidence in the code or pull request.
            </p>
          </div>
        </section>

        <section className="landing-section landing-offer landing-offer--product" id="renderrepo" aria-labelledby="product-heading" tabIndex={-1}>
          <div className="landing-section-inner">
            <p className="landing-section-eyebrow">Software product</p>
            <h2 id="product-heading" className="landing-section-title">
              RenderRepo: check whether a code change matches what was approved
            </h2>
            <p className="landing-proof-lede">
              RenderRepo is for developers, AI coding tools, and GitHub pull requests. It reviews a software change
              against the original approved requirements and evidence from the code or pull request—so acceptance is
              based on what the customer agreed to, not only whether the code runs.
            </p>
            <div className="landing-hero-ctas landing-product-ctas">
              <Link to="/register" className="landing-cta landing-cta-primary">
                Start with RenderRepo
              </Link>
              <a href="#example-review" className="landing-cta landing-cta-secondary">
                See an example review
              </a>
              <a href={mailtoFor("review")} className="landing-cta landing-cta-tertiary">
                Ask about a software acceptance review
              </a>
            </div>
            <p className="landing-hero-trust">{reviewPricingLine()}</p>
          </div>
        </section>

        <section className="landing-section landing-section--surface" id="example-review" aria-labelledby="example-heading" tabIndex={-1}>
          <div className="landing-section-inner">
            <p className="landing-section-eyebrow">Example review</p>
            <h2 id="example-heading" className="landing-section-title">
              The code still runs. The product promise does not.
            </h2>
            <article className="landing-pain-card">
              <h3 className="landing-pain-card-title">Receipt notification removed</h3>
              <p>This pull request removes the receipt call after successful payment.</p>
              <p>Your Company Brain says: “Customers receive a receipt after a successful payment.”</p>
              <p>Context: Inferred · Product promise</p>
              <p>Evidence: src/checkout/complete.ts</p>
            </article>
          </div>
        </section>

        <section className="landing-section" id="contact" aria-labelledby="contact-heading" tabIndex={-1}>
          <div className="landing-section-inner">
            <p className="landing-section-eyebrow">Contact</p>
            <h2 id="contact-heading" className="landing-section-title">Tell us which path you need</h2>
            <p className="landing-proof-lede">
              Email <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> and choose the path that fits. Existing
              accounts can still <Link to="/login">sign in</Link> or <Link to="/register">register</Link> for
              RenderRepo.
            </p>
            <div className="landing-pain-grid landing-contact-grid">
              <article className="landing-pain-card">
                <h3 className="landing-pain-card-title">Workflow help</h3>
                <p>Owner-run business, Indianapolis and nearby. One repetitive process you want to improve.</p>
                <p className="landing-cta-row">
                  <a href={mailtoFor("workflow")} className="landing-cta landing-cta-primary">
                    Book a 20-minute workflow call
                  </a>
                </p>
              </article>
              <article className="landing-pain-card">
                <h3 className="landing-pain-card-title">Software acceptance review</h3>
                <p>A pull request or delivery that should be checked against approved requirements.</p>
                <p className="landing-cta-row">
                  <a href={mailtoFor("review")} className="landing-cta landing-cta-secondary">
                    Request an acceptance review
                  </a>
                </p>
              </article>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
